import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STATUS_CODES } from "constants/index";

type FetchPageArgs<TFilters> = {
  page: number;
  limit: number;
  search: string;
  filters: TFilters;
};

type FetchPageResponse<TItem> = {
  status: number;
  data?: {
    result?: TItem[];
    count?: number;
  } | TItem[] | null;
};

type UseInfiniteApiListOptions<TItem, TFilters> = {
  enabled?: boolean;
  limit?: number;
  filters: TFilters;
  getItemKey?: (item: TItem) => string | number;
  fetchPage: (args: FetchPageArgs<TFilters>) => Promise<FetchPageResponse<TItem>>;
};

const DEFAULT_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 250;

export default function useInfiniteApiList<TItem, TFilters>({
  enabled = true,
  limit = DEFAULT_LIMIT,
  filters,
  getItemKey,
  fetchPage,
}: UseInfiniteApiListOptions<TItem, TFilters>) {
  const [items, setItems] = useState<TItem[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestIdRef = useRef(0);
  const fetchPageRef = useRef(fetchPage);
  const filtersRef = useRef(filters);
  const getItemKeyRef = useRef(getItemKey);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const hasMore = items.length < count;

  useEffect(() => {
    fetchPageRef.current = fetchPage;
  }, [fetchPage]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    getItemKeyRef.current = getItemKey;
  }, [getItemKey]);

  const mergeItems = useCallback((currentItems: TItem[], nextItems: TItem[]) => {
    const currentGetItemKey = getItemKeyRef.current;

    if (!currentGetItemKey) {
      return [...currentItems, ...nextItems];
    }

    const existingKeys = new Set(currentItems.map(currentGetItemKey));
    return [
      ...currentItems,
      ...nextItems.filter((item) => !existingKeys.has(currentGetItemKey(item))),
    ];
  }, []);

  const loadPage = useCallback(
    async (nextPage: number, shouldAppend: boolean) => {
      if (!enabled) {
        setItems([]);
        setCount(0);
        setPage(1);
        return;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (shouldAppend) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await fetchPageRef.current({
        page: nextPage,
        limit,
        search: debouncedSearch,
        filters: filtersRef.current,
      });

      if (requestIdRef.current !== requestId) {
        return;
      }

      const responseData = response.data;
      const nextItems = Array.isArray(responseData)
        ? responseData
        : responseData?.result ?? [];
      const nextCount = Array.isArray(responseData)
        ? nextItems.length
        : responseData?.count ?? nextItems.length;

      if (response.status === STATUS_CODES.OK) {
        setItems((currentItems) =>
          shouldAppend ? mergeItems(currentItems, nextItems) : nextItems,
        );
        setCount(nextCount);
        setPage(nextPage);
      }

      setLoading(false);
      setLoadingMore(false);
    },
    [debouncedSearch, enabled, limit, mergeItems],
  );

  const loadFirstPage = useCallback(() => {
    return loadPage(1, false);
  }, [loadPage]);

  const loadNextPage = useCallback(() => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    return loadPage(page + 1, true);
  }, [hasMore, loadPage, loading, loadingMore, page]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    void loadFirstPage();
  }, [filtersKey, debouncedSearch, loadFirstPage]);

  const reset = useCallback(() => {
    setItems([]);
    setCount(0);
    setPage(1);
    requestIdRef.current += 1;
  }, []);

  return {
    items,
    search,
    setSearch,
    page,
    count,
    hasMore,
    loading,
    loadingMore,
    reset,
    loadFirstPage,
    loadNextPage,
  };
}
